/**
 * web/src/games/nocturne/NocturnePanel.tsx — Nocturne, the two-device deduction
 * game, as a sub-tab of Us.
 *
 * Ported from the design pack's reference client: same engine (./engine.ts),
 * same Case Host model — the device that starts a case holds the secrets and
 * broadcasts public state after every mutation; the partner's device renders
 * only from those broadcasts. What was deleted from the reference: its pairing
 * code, its invite URLs, its localStorage credential store and its icon
 * package — pairing is Pear's own session, transport is the couple room's
 * transient `nocturne` frames (./net.ts), icons are hand-drawn (./icons.tsx).
 *
 * All colour comes from design/tokens.css via nocturne.css; no hex here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { session } from '../../shell/bridge';
import { NOCTURNE_ICONS } from './icons';
import {
  AP_DAY,
  COVER_MAX,
  ESCAPE_MAX,
  FALSE_MAX,
  GUARD_MAX,
  INTER_COST,
  LOCS,
  ROUNDS,
  applyDayIntent,
  applyNightAction,
  newGame,
  otherSeat,
  reasonTextFor,
  startDuetGame,
  toPublicState,
  turnOf,
  type DayAction,
  type Difficulty,
  type Game,
  type JournalEntry,
  type Mode,
  type NightAct,
  type PublicState,
  type Seat,
} from './engine';
import { useNocturneSession } from './net';
/* Card psHEfEkK9cT2 — the shared duet win asks the app-wide layer for a burst. */
import { celebrateOnce } from '../../lib/celebrate';
import './nocturne.css';

/* ── series tally (local-only score, spec §5; not a credential) ────── */

interface Series {
  me: number;
  partner: number;
}
interface CoopTally {
  solved: number;
  lost: number;
}

const LS_SERIES = 'pear.nocturne.series';
const LS_COOP = 'pear.nocturne.coop';

function loadSeries(): Series {
  try {
    const raw = localStorage.getItem(LS_SERIES);
    if (raw) {
      const s = JSON.parse(raw) as Partial<Series>;
      if (typeof s.me === 'number' && typeof s.partner === 'number') return { me: s.me, partner: s.partner };
    }
  } catch { /* fresh device */ }
  return { me: 0, partner: 0 };
}

function loadCoop(): CoopTally {
  try {
    const raw = localStorage.getItem(LS_COOP);
    if (raw) {
      const s = JSON.parse(raw) as Partial<CoopTally>;
      if (typeof s.solved === 'number' && typeof s.lost === 'number') return { solved: s.solved, lost: s.lost };
    }
  } catch { /* fresh device */ }
  return { solved: 0, lost: 0 };
}

/* ── board atoms ────────────────────────────────────────────────────── */

interface BoardData {
  marksCanvass: (boolean | null)[];
  marksInter: (number | null)[];
  phantomView?: boolean;
  reveal?: boolean;
  crimeLoc?: number;
  cover?: number[];
  falseLeads?: number[];
}

function NcBoard({
  data,
  onPick,
  selected,
}: {
  data: BoardData;
  onPick?: (i: number) => void;
  selected?: number | null;
}) {
  return (
    <div className="nc-board" role="grid" aria-label="The city — nine locations">
      {LOCS.map((loc, i) => {
        const isCrime = (data.phantomView || data.reveal) && data.crimeLoc === i;
        const isLead = (data.phantomView || data.reveal) && (data.falseLeads ?? []).includes(i);
        const covers = data.cover?.[i] ?? 0;
        const disabled = onPick === undefined;
        const Icon = NOCTURNE_ICONS[loc.icon];
        const inter = data.marksInter[i];
        const canv = data.marksCanvass[i];
        return (
          <button
            key={loc.name}
            type="button"
            role="gridcell"
            className={`nc-tile${selected === i ? ' is-selected' : ''}`}
            disabled={disabled}
            onClick={onPick ? () => onPick(i) : undefined}
            aria-label={loc.name}
          >
            <span className="nc-tile-top">
              <span className="nc-tile-icon">
                <Icon size={26} />
              </span>
              <span className="nc-tile-badges">
                {isCrime && <span className="nc-badge nc-badge-crime" title="the scene of the crime">✦</span>}
                {isLead && <span className="nc-badge nc-badge-lead" title="false lead">◆</span>}
              </span>
            </span>
            <span className="nc-tile-name">{loc.name}</span>
            {data.phantomView || data.reveal ? (
              <span className="nc-tile-covers">
                {covers > 0 ? `${'▮'.repeat(covers)} covered` : '·'}
              </span>
            ) : (
              <span className="nc-tile-dots">
                {inter !== null ? (
                  Array.from({ length: 3 }).map((_, d) => (
                    <span key={d} className={`nc-dot${d < inter ? ' nc-dot-on' : ''}`} />
                  ))
                ) : canv !== null ? (
                  <>
                    <span className={`nc-dot nc-dot-single${canv ? ' nc-dot-hot' : ''}`} />
                    <span className="nc-tile-canvass-label">{canv ? 'whispers' : 'quiet'}</span>
                  </>
                ) : (
                  Array.from({ length: 3 }).map((_, d) => <span key={d} className="nc-dot" />)
                )}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Gem meter (escape) and pip meters (actions / guards). */
function NcMeter({ filled, total, kind }: { filled: number; total: number; kind: 'gem' | 'pip' }) {
  return (
    <span className="nc-meter" aria-label={`${filled} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={`${kind === 'gem' ? 'nc-gem' : 'nc-pip'}${i < filled ? ' is-on' : ''}`} />
      ))}
    </span>
  );
}

function NcMeters({ ap, escape, guardsUsed }: { ap: number; escape: number; guardsUsed: number }) {
  return (
    <div className="nc-meters">
      <span className="nc-meter-label">
        actions <NcMeter filled={ap} total={AP_DAY} kind="pip" />
      </span>
      <span className="nc-meter-label">
        escape <NcMeter filled={escape} total={ESCAPE_MAX} kind="gem" />
      </span>
      <span className="nc-meter-label">guards left {GUARD_MAX - guardsUsed}</span>
    </div>
  );
}

function NcJournal({ entries }: { entries: JournalEntry[] }) {
  return (
    <div className="nc-journal">
      <div className="nc-journal-title">Case journal</div>
      <div className="nc-journal-list">
        {entries.length === 0 && <div className="nc-journal-empty">The city waits. Rain on cobblestones.</div>}
        {entries.map((j, idx) => (
          <div key={idx} className={`nc-journal-entry nc-tone-${j.tone}`}>
            <span className="nc-journal-round">D{j.round} · </span>
            {j.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function NcBanner({ status, peerOnline, partnerName }: { status: string; peerOnline: boolean; partnerName: string }) {
  if (status === 'live' && peerOnline) return null;
  const msg =
    status === 'offline'
      ? 'not connected — Nocturne needs your pair session'
      : status === 'connecting'
        ? 'reaching the city…'
        : `${partnerName} is away`;
  return <div className="nc-banner">{msg}</div>;
}

function NcButton({
  children,
  onClick,
  variant = 'ghost',
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'rose';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`nc-btn nc-btn-${variant}`}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function NcActionCard({
  title,
  desc,
  onClick,
  disabled,
  accent,
}: {
  title: string;
  desc: string;
  onClick?: () => void;
  disabled?: boolean;
  accent?: 'rose';
}) {
  return (
    <button type="button" className={`nc-action-card${accent === 'rose' ? ' nc-accent-rose' : ''}`} disabled={disabled} onClick={onClick}>
      <span className="nc-action-title">{title}</span>
      <span className="nc-action-desc">{desc}</span>
    </button>
  );
}

function NcModal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function NcRulesModal({ onClose }: { onClose: () => void }) {
  return (
    <NcModal onClose={onClose}>
      <div className="nc-modal-title">How to play</div>
      <div className="nc-rules">
        <p>
          <span className="nc-inline-gold">Duel</span> — one of you hides, one of you hunts. Same case, two screens.
        </p>
        <p>
          <span className="nc-inline-rose">The Phantom</span> secretly picks one of nine locations on their own device:
          the scene of the crime. The Detective never sees it, only what it leaves behind.
        </p>
        <p>
          Each night the Phantom does one thing: <b>flee</b> (three flights and they're gone), <b>cover tracks</b> at
          a location (its story dims by a star, 3 uses), <b>plant a false lead</b> (a location that reads ★★★, 2
          uses), or <b>lie low</b>.
        </p>
        <p>
          Each day the <span className="nc-inline-gold">Detective</span> gets two actions: <b>canvass</b> a street
          (hot or cold), <b>interrogate in depth</b> (★★★ at the scene, ★★ next door, less if the tracks were
          covered), or <b>guard the gates</b> (push the escape back a step, 2 uses).
        </p>
        <p>
          The scene whispers to the streets beside it. A false lead stands alone. Four days, one accusation — wrong,
          or never made, and the Phantom wins.
        </p>
        <p>
          <span className="nc-inline-rose">Duet</span> — you're both detectives, hunting the app's Phantom. It draws
          each night from a deck you see before the case opens. You each take one action a day on your own screen; a
          deep interrogation takes both of you. One shared accusation to win.
        </p>
        <p>
          You're paired with one partner, always. Whoever starts a Duel case plays the Phantom; start the next case to
          swap.
        </p>
      </div>
      <div className="nc-modal-actions">
        <NcButton variant="primary" onClick={onClose}>Understood</NcButton>
      </div>
    </NcModal>
  );
}

/* ── screens ────────────────────────────────────────────────────────── */

function NcWaiting({
  kicker,
  title,
  note,
  board,
  journal,
}: {
  kicker: string;
  title: string;
  note: string;
  board?: BoardData;
  journal?: JournalEntry[];
}) {
  return (
    <div className="nc-screen">
      <div className="nc-kicker">{kicker}</div>
      <div className="nc-headline">{title}</div>
      <p className="nc-note">{note}</p>
      {board && (
        <div className="nc-board-wrap">
          <NcBoard data={board} />
        </div>
      )}
      {journal && <NcJournal entries={journal} />}
    </div>
  );
}

function NcPhantomSetup({ onConfirm }: { onConfirm: (loc: number) => void }) {
  const [pending, setPending] = useState<number | null>(null);
  return (
    <div className="nc-screen">
      <div className="nc-kicker">you · the phantom · choose the scene of the crime</div>
      <div className="nc-board-wrap">
        <NcBoard
          data={{ marksCanvass: Array(9).fill(null), marksInter: Array(9).fill(null) }}
          onPick={(i) => setPending(i)}
          selected={pending}
        />
      </div>
      <p className="nc-note">
        This location is your secret — it never leaves this device. Its streets will whisper to your partner, and to
        the eight streets beside it.
      </p>
      <NcButton variant="rose" disabled={pending === null} onClick={() => onConfirm(pending as number)}>
        Seal the secret
      </NcButton>
    </div>
  );
}

function NcPhantomNight({
  g,
  onNightAction,
}: {
  g: Game;
  onNightAction: (act: NightAct, target: number | null) => void;
}) {
  const [nightAct, setNightAct] = useState<NightAct | null>(null);
  const [nightTarget, setNightTarget] = useState<number | null>(null);
  const needTarget = nightAct === 'cover' || nightAct === 'lead';
  return (
    <div className="nc-screen">
      <div className="nc-kicker">
        night {g.round} of {ROUNDS} · you · the phantom
      </div>
      <div className="nc-board-wrap">
        <NcBoard
          data={{
            marksCanvass: g.marksCanvass,
            marksInter: g.marksInter,
            phantomView: true,
            crimeLoc: g.crimeLoc,
            cover: g.cover,
            falseLeads: g.falseLeads,
          }}
          onPick={needTarget ? (i) => setNightTarget(i) : undefined}
          selected={nightTarget}
        />
      </div>
      <div className="nc-meters">
        <span className="nc-meter-label">
          escape <NcMeter filled={g.escape} total={ESCAPE_MAX} kind="gem" />
        </span>
        <span className="nc-meter-label">covers left {COVER_MAX - g.coverUses}</span>
        <span className="nc-meter-label">false leads left {FALSE_MAX - g.falseUses}</span>
      </div>
      {nightAct === null ? (
        <div className="nc-action-grid">
          <NcActionCard title="Flee" desc="Draw closer to escape. Three flights and you're gone." onClick={() => setNightAct('flee')} />
          <NcActionCard title="Cover Tracks" desc="Muddy the story at one location." onClick={() => setNightAct('cover')} disabled={g.coverUses >= COVER_MAX} />
          <NcActionCard title="False Lead" desc="Stage a false sighting anywhere." onClick={() => setNightAct('lead')} disabled={g.falseUses >= FALSE_MAX} />
          <NcActionCard title="Lie Low" desc="Do nothing. Let them doubt." onClick={() => setNightAct('low')} />
        </div>
      ) : (
        <div className="nc-confirm">
          <div className="nc-confirm-text">
            {nightAct === 'flee' && 'Slip through the rain toward the city gates?'}
            {nightAct === 'cover' && (nightTarget === null ? 'Choose a location to cover.' : `Cover tracks at ${LOCS[nightTarget].name}?`)}
            {nightAct === 'lead' && (nightTarget === null ? 'Choose where to plant the false story.' : `Plant the false lead at ${LOCS[nightTarget].name}?`)}
            {nightAct === 'low' && 'Stay in the shadows and do nothing?'}
          </div>
          <div className="nc-confirm-actions">
            <NcButton onClick={() => { setNightAct(null); setNightTarget(null); }}>Back</NcButton>
            <NcButton
              variant="rose"
              disabled={needTarget && nightTarget === null}
              onClick={() => {
                const a = nightAct;
                const t = nightTarget;
                setNightAct(null);
                setNightTarget(null);
                onNightAction(a, t);
              }}
            >
              Confirm
            </NcButton>
          </div>
        </div>
      )}
    </div>
  );
}

function NcNightTheater({ round, onWake }: { round: number; onWake: () => void }) {
  return (
    <div className="nc-screen nc-screen-center">
      <div className="nc-kicker">
        night {round} of {ROUNDS}
      </div>
      <div className="nc-headline nc-headline-lg">The Phantom moves unseen</div>
      <p className="nc-note">
        Rain on the rooftops, silence in the streets. Whatever happened tonight, only the city knows.
      </p>
      <NcButton variant="primary" onClick={onWake}>
        Wake to day {round}
      </NcButton>
    </div>
  );
}

interface DayView {
  round: number;
  ap: number;
  escape: number;
  guardsUsed: number;
  accusedUsed: boolean;
  marksCanvass: (boolean | null)[];
  marksInter: (number | null)[];
  journal: JournalEntry[];
  turn: Seat | null;
}

function NcDayScreen({
  header,
  view,
  ownerLabel,
  interactive,
  showAccuse,
  showEndDay,
  note,
  onAction,
}: {
  header: string;
  view: DayView;
  ownerLabel: string | null;
  interactive: boolean;
  showAccuse: boolean;
  showEndDay: boolean;
  note?: string;
  onAction: (a: DayAction, loc?: number) => void;
}) {
  const [dayMode, setDayMode] = useState<null | 'canvass' | 'interrogate' | 'accuse'>(null);
  const [dayTarget, setDayTarget] = useState<number | null>(null);
  const [confirmAccuse, setConfirmAccuse] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const picking = dayMode !== null;

  return (
    <div className="nc-screen">
      <div className="nc-kicker">{header}</div>
      {ownerLabel && <div className="nc-owner">{ownerLabel}</div>}
      <NcMeters ap={view.ap} escape={view.escape} guardsUsed={view.guardsUsed} />
      <div className="nc-board-wrap">
        <NcBoard
          data={{ marksCanvass: view.marksCanvass, marksInter: view.marksInter }}
          onPick={interactive && picking ? (i) => setDayTarget(i) : undefined}
          selected={dayTarget}
        />
      </div>

      {picking ? (
        <div className="nc-confirm">
          <div className="nc-confirm-text">
            {dayMode === 'canvass' && (dayTarget === null ? 'Choose a street to canvass (1 action).' : `Canvass ${LOCS[dayTarget].name}?`)}
            {dayMode === 'interrogate' && (dayTarget === null ? 'Choose a location to interrogate (2 actions).' : `Interrogate ${LOCS[dayTarget].name}?`)}
            {dayMode === 'accuse' && (dayTarget === null ? 'Name the scene of the crime. Choose a location.' : `Accuse ${LOCS[dayTarget].name}? A wrong accusation ends everything.`)}
          </div>
          <div className="nc-confirm-actions">
            <NcButton onClick={() => { setDayMode(null); setDayTarget(null); }}>Back</NcButton>
            {dayMode === 'canvass' && (
              <NcButton variant="primary" disabled={dayTarget === null} onClick={() => { const t = dayTarget; setDayMode(null); setDayTarget(null); onAction('canvass', t as number); }}>Canvass</NcButton>
            )}
            {dayMode === 'interrogate' && (
              <NcButton variant="primary" disabled={dayTarget === null} onClick={() => { const t = dayTarget; setDayMode(null); setDayTarget(null); onAction('interrogate', t as number); }}>Interrogate</NcButton>
            )}
            {dayMode === 'accuse' && (
              <NcButton variant="rose" disabled={dayTarget === null} onClick={() => setConfirmAccuse(true)}>Accuse</NcButton>
            )}
          </div>
        </div>
      ) : interactive ? (
        <div className="nc-action-grid">
          <NcActionCard title="Canvass" desc="Knock on doors. Hot or cold. (1)" onClick={() => setDayMode('canvass')} disabled={view.ap < 1} />
          <NcActionCard title="Interrogate" desc="Question in depth. It takes both actions. (2)" onClick={() => setDayMode('interrogate')} disabled={view.ap < INTER_COST} />
          <NcActionCard title="Guard the Gates" desc="Set the Phantom's escape back one step. (1)" onClick={() => onAction('guard')} disabled={view.ap < 1 || view.guardsUsed >= GUARD_MAX} />
          {showAccuse ? (
            <NcActionCard title="Accuse" desc="Name the scene. Once. Risk everything." onClick={() => setDayMode('accuse')} disabled={view.accusedUsed} accent="rose" />
          ) : (
            <span />
          )}
        </div>
      ) : (
        note && <p className="nc-note">{note}</p>
      )}

      <NcJournal entries={view.journal} />

      <div className="nc-row-actions">
        <NcButton onClick={() => setShowRules(true)}>Rules</NcButton>
        {showEndDay && <NcButton variant="primary" onClick={() => setConfirmEnd(true)}>End the day</NcButton>}
      </div>

      {confirmAccuse && dayTarget !== null && (
        <NcModal onClose={() => setConfirmAccuse(false)}>
          <div className="nc-modal-title">
            Accuse <span className="nc-inline-rose">{LOCS[dayTarget].name}</span>?
          </div>
          <p className="nc-modal-text">One accusation. If you are wrong, the Phantom vanishes and wins the night.</p>
          <div className="nc-modal-actions">
            <NcButton onClick={() => setConfirmAccuse(false)}>Back</NcButton>
            <NcButton
              variant="rose"
              onClick={() => {
                const t = dayTarget;
                setConfirmAccuse(false);
                setDayMode(null);
                setDayTarget(null);
                onAction('accuse', t);
              }}
            >
              Make the accusation
            </NcButton>
          </div>
        </NcModal>
      )}
      {confirmEnd && (
        <NcModal onClose={() => setConfirmEnd(false)}>
          <div className="nc-modal-title">End day {view.round}?</div>
          <p className="nc-modal-text">
            {view.ap > 0 && `You still have ${view.ap} action${view.ap > 1 ? 's' : ''} — they do not carry over. `}
            {view.round >= ROUNDS
              ? 'This is the last day. If no one accuses, the Phantom slips away forever.'
              : 'The night belongs to the Phantom.'}
          </p>
          <div className="nc-modal-actions">
            <NcButton onClick={() => setConfirmEnd(false)}>Back</NcButton>
            <NcButton variant="primary" onClick={() => { setConfirmEnd(false); onAction('end_day'); }}>End the day</NcButton>
          </div>
        </NcModal>
      )}
      {showRules && <NcRulesModal onClose={() => setShowRules(false)} />}
    </div>
  );
}

function NcOverScreen({
  winner,
  reason,
  reveal,
  marksCanvass,
  marksInter,
  headline,
  reasonText,
  footerLine,
  rematchLabel,
  onRematch,
  onHome,
}: {
  winner: 'phantom' | 'detective';
  reason: string;
  reveal: { crimeLoc: number; cover: number[]; falseLeads: number[]; nightLog: { round: number; text: string }[] };
  marksCanvass: (boolean | null)[];
  marksInter: (number | null)[];
  headline: string;
  reasonText: string;
  footerLine: string;
  rematchLabel?: string;
  onRematch?: () => void;
  onHome: () => void;
}) {
  const detWon = winner === 'detective';
  return (
    <div className="nc-screen">
      <div className="nc-kicker">the night is over</div>
      <div className={`nc-headline${detWon ? ' nc-headline-gold' : ' nc-headline-rose'}`}>{headline}</div>
      <p className="nc-note">{reasonText}</p>
      <div className="nc-board-wrap">
        <div className="nc-reveal-label">the scene of the crime — {LOCS[reveal.crimeLoc].name}</div>
        <NcBoard
          data={{ marksCanvass, marksInter, reveal: true, crimeLoc: reveal.crimeLoc, cover: reveal.cover, falseLeads: reveal.falseLeads }}
        />
        <div className="nc-nightlog">
          {reveal.nightLog.map((n, idx) => (
            <div key={idx} className="nc-nightlog-entry">
              <span className="nc-journal-round">N{n.round} · </span>
              {n.text}
            </div>
          ))}
        </div>
      </div>
      <div className="nc-footer-line">{footerLine}</div>
      <div className="nc-row-actions">
        {onRematch && rematchLabel && <NcButton variant="primary" onClick={onRematch}>{rematchLabel}</NcButton>}
        <NcButton onClick={onHome}>Home</NcButton>
      </div>
    </div>
  );
}

/* ── the panel ──────────────────────────────────────────────────────── */

export function NocturnePanel({ members }: { members: { memberId: string; displayName: string }[] }) {
  const net = useNocturneSession();
  const me = session();

  const [screen, setScreen] = useState<'lobby' | 'case'>('lobby');
  const [mode, setMode] = useState<Mode | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [myRole, setMyRole] = useState<'phantom' | 'detective' | 'coop' | null>(null);
  const [g, setG] = useState<Game | null>(null);
  const [pub, setPub] = useState<PublicState | null>(null);
  const [duetAwake, setDuetAwake] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [series, setSeries] = useState<Series>(loadSeries);
  const [coop, setCoop] = useState<CoopTally>(loadCoop);

  const gRef = useRef<Game | null>(null);
  const isHostRef = useRef(false);
  const modeRef = useRef<Mode>('duel');
  const scoredRef = useRef(false);
  /* Card psHEfEkK9cT2 — one key per duet case, so consecutive solved nights
     each earn their own burst and none reuses the last one. */
  const duetCaseRef = useRef(0);

  const mySeat: Seat | null = net.seat ?? me?.role ?? null;
  const myName = members.find((m) => m.memberId === me?.memberId)?.displayName ?? 'You';
  const partnerName = members.find((m) => m.memberId !== me?.memberId)?.displayName ?? 'your partner';

  const seatRef = useRef<Seat | null>(mySeat);
  seatRef.current = mySeat;
  const myNameRef = useRef(myName);
  myNameRef.current = myName;
  const partnerNameRef = useRef(partnerName);
  partnerNameRef.current = partnerName;

  const seatName = useCallback((s: Seat): string => {
    return s === seatRef.current ? myNameRef.current : partnerNameRef.current;
  }, []);

  /* ── host plumbing ── */

  const commit = useCallback(
    (ng: Game) => {
      gRef.current = ng;
      setG(ng);
      net.send({ kind: 'public_state', state: toPublicState(ng, modeRef.current) });
    },
    [net],
  );

  const submitIntent = useCallback(
    (action: DayAction, loc?: number) => {
      const by = seatRef.current;
      if (isHostRef.current) {
        const g0 = gRef.current;
        if (!g0 || !by) return;
        const ng = applyDayIntent(g0, modeRef.current, { action, loc, by }, seatName);
        if (ng) commit(ng);
      } else if (by) {
        net.send({ kind: 'intent', action, loc, by });
      }
    },
    [commit, net, seatName],
  );

  /* ── incoming frames ── */

  useEffect(() => {
    return net.subscribe((f) => {
      switch (f.kind) {
        case 'new_case': {
          setIsHost(false);
          isHostRef.current = false;
          setMode(f.mode);
          modeRef.current = f.mode;
          setMyRole(f.mode === 'duel' ? 'detective' : 'coop');
          setG(null);
          gRef.current = null;
          setPub(null);
          setDuetAwake(false);
          scoredRef.current = false;
          setScreen('case');
          break;
        }
        case 'intent': {
          if (!isHostRef.current) return;
          const g0 = gRef.current;
          if (!g0) return;
          const ng = applyDayIntent(g0, modeRef.current, f, seatName);
          if (ng) commit(ng);
          break;
        }
        case 'public_state': {
          setPub(f.state);
          break;
        }
        case 'peer_online': {
          // The reconnect path: the Case Host re-broadcasts the snapshot.
          if (isHostRef.current && gRef.current) {
            net.send({ kind: 'public_state', state: toPublicState(gRef.current, modeRef.current) });
          }
          break;
        }
        case 'peer_offline':
          break;
      }
    });
  }, [net, commit, seatName]);

  /* Duet: a fresh night on the wire resets the local wake state. */
  useEffect(() => {
    if (pub && pub.phase === 'night') setDuetAwake(false);
  }, [pub]);

  /* Series bookkeeping — once per case, from the reveal. */
  useEffect(() => {
    const st = isHost ? g : pub;
    if (!st || st.phase !== 'over' || scoredRef.current) return;
    scoredRef.current = true;
    if (mode === 'duel' && myRole) {
      const iWon = myRole === 'phantom' ? st.winner === 'phantom' : st.winner === 'detective';
      setSeries((prev) => {
        const next = { me: prev.me + (iWon ? 1 : 0), partner: prev.partner + (iWon ? 0 : 1) };
        try { localStorage.setItem(LS_SERIES, JSON.stringify(next)); } catch { /* private mode */ }
        return next;
      });
    } else if (mode === 'duet') {
      /* Card psHEfEkK9cT2: a duet is the one mode you genuinely win TOGETHER
         — both accusations land on the Phantom. Celebrate the solved case,
         once (scoredRef already gates one pass per case; the counter gives the
         layer a per-case key so a rematch cannot reuse the last one). */
      duetCaseRef.current += 1;
      if (st.winner === 'detective') celebrateOnce(`nocturne-duet:${duetCaseRef.current}`);
      setCoop((prev) => {
        const next = st.winner === 'detective' ? { solved: prev.solved + 1, lost: prev.lost } : { solved: prev.solved, lost: prev.lost + 1 };
        try { localStorage.setItem(LS_COOP, JSON.stringify(next)); } catch { /* private mode */ }
        return next;
      });
    }
  }, [isHost, g, pub, mode, myRole]);

  /* ── case starters ── */

  const startDuelAsPhantom = useCallback(() => {
    if (net.status !== 'live' || !seatRef.current) return;
    setIsHost(true);
    isHostRef.current = true;
    setMode('duel');
    modeRef.current = 'duel';
    setMyRole('phantom');
    setG(null);
    gRef.current = null;
    setPub(null);
    scoredRef.current = false;
    net.send({ kind: 'new_case', mode: 'duel', difficulty: 'first', by: seatRef.current });
    setScreen('case');
  }, [net]);

  const startDuetHost = useCallback(
    (diff: Difficulty) => {
      if (net.status !== 'live' || !seatRef.current) return;
      const ng = startDuetGame(diff);
      setIsHost(true);
      isHostRef.current = true;
      setMode('duet');
      modeRef.current = 'duet';
      setMyRole('coop');
      setPub(null);
      setDuetAwake(false);
      scoredRef.current = false;
      net.send({ kind: 'new_case', mode: 'duet', difficulty: diff, by: seatRef.current });
      commit(ng);
      setScreen('case');
    },
    [commit, net],
  );

  const confirmCrime = useCallback(
    (loc: number) => {
      const ng = newGame(loc);
      ng.phase = 'night';
      commit(ng);
    },
    [commit],
  );

  const doNightAction = useCallback(
    (act: NightAct, target: number | null) => {
      const g0 = gRef.current;
      if (!g0) return;
      const ng = applyNightAction(g0, act, target);
      if (ng) commit(ng);
    },
    [commit],
  );

  const leaveCase = useCallback(() => {
    setG(null);
    gRef.current = null;
    setPub(null);
    setMode(null);
    setMyRole(null);
    setIsHost(false);
    isHostRef.current = false;
    setScreen('lobby');
  }, []);

  /* ── render: lobby ── */

  if (screen === 'lobby') {
    return (
      <div className="nc-root">
        <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
        <div className="nc-wordmark">Nocturne</div>
        <div className="nc-subword">a game of secrets for two</div>
        <div className={`nc-presence${net.peerOnline ? ' is-here' : ''}`}>
          {net.peerOnline ? `${partnerName} is here` : `${partnerName} is away`}
        </div>
        <div className="nc-lobby-cards">
          <NcActionCard
            title="The Duel — start a case"
            desc="You be the Phantom this time. The secret stays on your device; your partner hunts it on theirs. Start the next case to swap."
            onClick={startDuelAsPhantom}
            disabled={net.status !== 'live'}
          />
          <NcActionCard
            title="Duet — First Case"
            desc="Work the case together against a cautious Phantom — 2 flights · 1 cover · 1 false lead · 1 quiet night."
            onClick={() => startDuetHost('first')}
            disabled={net.status !== 'live'}
            accent="rose"
          />
          <NcActionCard
            title="Duet — The Phantom's City"
            desc="A bolder Phantom — 3 flights · 2 covers · 1 false lead. Escape is possible; guard the gates."
            onClick={() => startDuetHost('city')}
            disabled={net.status !== 'live'}
            accent="rose"
          />
        </div>
        {(series.me > 0 || series.partner > 0) && (
          <div className="nc-footer-line">
            You {series.me} — {series.partner} {partnerName}
          </div>
        )}
        {(coop.solved > 0 || coop.lost > 0) && (
          <div className="nc-footer-line">
            Cases solved {coop.solved} · Phantoms escaped {coop.lost}
          </div>
        )}
        <div className="nc-row-actions">
          <NcButton onClick={() => setShowRules(true)}>How to play</NcButton>
        </div>
        {showRules && <NcRulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  /* ── render: the Duel, Case Host (the Phantom's device) ── */

  if (mode === 'duel' && isHost) {
    if (!g) return <NcPhantomSetup onConfirm={confirmCrime} />;
    if (g.phase === 'night') return <NcPhantomNight g={g} onNightAction={doNightAction} />;
    if (g.phase === 'over') {
      const detWon = g.winner === 'detective';
      return (
        <NcOverScreen
          winner={g.winner ?? 'phantom'}
          reason={g.reason}
          reveal={{ crimeLoc: g.crimeLoc, cover: g.cover, falseLeads: g.falseLeads, nightLog: g.nightLog }}
          marksCanvass={g.marksCanvass}
          marksInter={g.marksInter}
          headline={detWon ? `${partnerName} unmasked the truth` : 'You kept the secret'}
          reasonText={reasonTextFor(g.reason, g.crimeLoc, false)}
          footerLine={`You ${series.me} — ${series.partner} ${partnerName}`}
          rematchLabel="New case — you be the Phantom again"
          onRematch={startDuelAsPhantom}
          onHome={leaveCase}
        />
      );
    }
    return (
      <div className="nc-root">
        <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
        <NcDayScreen
          header={`day ${g.round} of ${ROUNDS} · ${partnerName} · the detective`}
          view={{
            round: g.round, ap: g.ap, escape: g.escape, guardsUsed: g.guardsUsed,
            accusedUsed: g.accusedUsed, marksCanvass: g.marksCanvass, marksInter: g.marksInter,
            journal: g.journal, turn: null,
          }}
          ownerLabel={`${partnerName} is working the case — every move appears here.`}
          interactive={false}
          showAccuse={false}
          showEndDay={false}
          onAction={() => {}}
        />
      </div>
    );
  }

  /* ── render: the Duel, guest (the Detective's device) ── */

  if (mode === 'duel' && !isHost) {
    if (!pub) {
      return (
        <NcWaiting
          kicker="the case begins"
          title="The Phantom is choosing"
          note="Somewhere in the city, a scene is being sealed. Look away if you must."
        />
      );
    }
    if (pub.phase === 'night') {
      return (
        <div className="nc-root">
          <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
          <NcWaiting
            kicker={`night ${pub.round} of ${ROUNDS}`}
            title="The Phantom moves unseen"
            note="Rain on the rooftops, silence in the streets. Whatever happened tonight, only the city knows."
            board={{ marksCanvass: pub.marksCanvass, marksInter: pub.marksInter }}
            journal={pub.journal}
          />
        </div>
      );
    }
    if (pub.phase === 'over' && pub.reveal) {
      const detWon = pub.winner === 'detective';
      return (
        <NcOverScreen
          winner={pub.winner ?? 'phantom'}
          reason={pub.reason}
          reveal={pub.reveal}
          marksCanvass={pub.marksCanvass}
          marksInter={pub.marksInter}
          headline={detWon ? 'You unmasked the truth' : `${partnerName} kept the secret`}
          reasonText={reasonTextFor(pub.reason, pub.reveal.crimeLoc, false)}
          footerLine={`You ${series.me} — ${series.partner} ${partnerName}`}
          rematchLabel="New case — you be the Phantom"
          onRematch={startDuelAsPhantom}
          onHome={leaveCase}
        />
      );
    }
    return (
      <div className="nc-root">
        <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
        <NcDayScreen
          header={`day ${pub.round} of ${ROUNDS} · you · the detective`}
          view={{
            round: pub.round, ap: pub.ap, escape: pub.escape, guardsUsed: pub.guardsUsed,
            accusedUsed: pub.accusedUsed, marksCanvass: pub.marksCanvass, marksInter: pub.marksInter,
            journal: pub.journal, turn: null,
          }}
          ownerLabel={pub.ap >= 1 ? 'The city is yours. Two actions.' : 'The day is spent. End it, and the night returns.'}
          interactive={true}
          showAccuse={true}
          showEndDay={true}
          onAction={submitIntent}
        />
      </div>
    );
  }

  /* ── render: Duet, Case Host ── */

  if (mode === 'duet' && isHost && g) {
    if (g.phase === 'over') {
      const detWon = g.winner === 'detective';
      return (
        <NcOverScreen
          winner={g.winner ?? 'phantom'}
          reason={g.reason}
          reveal={{ crimeLoc: g.crimeLoc, cover: g.cover, falseLeads: g.falseLeads, nightLog: g.nightLog }}
          marksCanvass={g.marksCanvass}
          marksInter={g.marksInter}
          headline={detWon ? 'The case is closed' : 'The Phantom slipped away'}
          reasonText={reasonTextFor(g.reason, g.crimeLoc, true)}
          footerLine={`Cases solved ${coop.solved} · Phantoms escaped ${coop.lost}`}
          onHome={leaveCase}
        />
      );
    }
    if (g.phase === 'night' && !duetAwake) {
      return <NcNightTheater round={g.round} onWake={() => setDuetAwake(true)} />;
    }
    const turn = turnOf(g, 'duet');
    const myTurn = turn !== null && mySeat === turn;
    const ownerLabel = turn
      ? myTurn
        ? 'Now — you.'
        : `Now — ${seatName(turn)}.`
      : 'The day is spent. Talk it over, then end the day.';
    return (
      <div className="nc-root">
        <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
        <NcDayScreen
          header={`day ${g.round} of ${ROUNDS} · the case`}
          view={{
            round: g.round, ap: g.ap, escape: g.escape, guardsUsed: g.guardsUsed,
            accusedUsed: g.accusedUsed, marksCanvass: g.marksCanvass, marksInter: g.marksInter,
            journal: g.journal, turn,
          }}
          ownerLabel={ownerLabel}
          interactive={myTurn}
          showAccuse={true}
          showEndDay={true}
          note={myTurn ? undefined : `${partnerName} is choosing — the board will speak when they move.`}
          onAction={submitIntent}
        />
      </div>
    );
  }

  /* ── render: Duet, guest ── */

  if (mode === 'duet' && !isHost) {
    if (!pub) {
      return (
        <NcWaiting
          kicker="a case is opening"
          title="The night deck is shuffling"
          note={`${partnerName} is dealing the Phantom's moves. The deck is fair — the order is not.`}
        />
      );
    }
    if (pub.phase === 'over' && pub.reveal) {
      const detWon = pub.winner === 'detective';
      return (
        <NcOverScreen
          winner={pub.winner ?? 'phantom'}
          reason={pub.reason}
          reveal={pub.reveal}
          marksCanvass={pub.marksCanvass}
          marksInter={pub.marksInter}
          headline={detWon ? 'The case is closed' : 'The Phantom slipped away'}
          reasonText={reasonTextFor(pub.reason, pub.reveal.crimeLoc, true)}
          footerLine={`Cases solved ${coop.solved} · Phantoms escaped ${coop.lost}`}
          onHome={leaveCase}
        />
      );
    }
    if (pub.phase === 'night' && !duetAwake) {
      return <NcNightTheater round={pub.round} onWake={() => setDuetAwake(true)} />;
    }
    const turn = pub.turn ?? null;
    const myTurn = turn !== null && mySeat === turn;
    const ownerLabel = turn
      ? myTurn
        ? 'Now — you.'
        : `Now — ${turn === mySeat ? myName : partnerName}.`
      : 'The day is spent. Talk it over, then end the day.';
    return (
      <div className="nc-root">
        <NcBanner status={net.status} peerOnline={net.peerOnline} partnerName={partnerName} />
        <NcDayScreen
          header={`day ${pub.round} of ${ROUNDS} · the case`}
          view={{
            round: pub.round, ap: pub.ap, escape: pub.escape, guardsUsed: pub.guardsUsed,
            accusedUsed: pub.accusedUsed, marksCanvass: pub.marksCanvass, marksInter: pub.marksInter,
            journal: pub.journal, turn,
          }}
          ownerLabel={ownerLabel}
          interactive={myTurn}
          showAccuse={true}
          showEndDay={true}
          note={myTurn ? undefined : `${partnerName} is choosing — the board will speak when they move.`}
          onAction={submitIntent}
        />
      </div>
    );
  }

  /* fallback — mid-transition */
  return <NcWaiting kicker="…" title="The city holds its breath" note="" />;
}
